import { App } from '../../App';
import { differenceInSeconds } from 'date-fns';
import {
  FINGERPRINT_CONSTANTS_KEYS,
  getFingerprintDeviceId,
} from '../../utils/fingerprintDeviceId';
import { getFetchHeaders } from '../../utils/fetchHeaders';
import { isAlreadyExistsMobileError } from 'api/errors/ValidationError';
import { PhoneVerificationForm } from 'pages/PhoneVerification/PhoneVerification';
import { ROUTES } from 'utils/constants/constants_routes';
import { SESSION_PHONE_UPDATED_KEY } from 'pages/PhoneVerificationChangePhone/PhoneVerificationChangePhone';
import { useErrorManager } from 'InternalLibrary';
import { useNavigate } from 'react-router-dom';
import useClient from '../../hooks/useClient';

import { useState } from 'react';
import useIovation from '../../hooks/useIovation';

export const SESSION_LAST_PHONE_CHANGE_KEY = 'lastPhoneChangeSent';
const SESSION_LAST_SMS_SENT_KEY = 'lastSMSSent';
const COOLDOWN_SECONDS = 60;

const getSecondsLeft = (key: string): number => {
  const stored = sessionStorage.getItem(key);
  if (!stored || Number.isNaN(parseInt(stored))) return 0;
  const elapsed = differenceInSeconds(new Date().getTime(), parseInt(stored));
  return Math.max(0, COOLDOWN_SECONDS - elapsed);
};

const getAttempt = () => {
  const attempt = sessionStorage.getItem('phoneVerification_attempt');
  return attempt ? Number(attempt) : 1;
};

const KEY_ATTEMPT = 'phoneVerification_attempt';

const saveAttempt = (attempt: number) => {
  sessionStorage.setItem(KEY_ATTEMPT, `${attempt}`);
};

const deleteSessionStorageAttempt = () => {
  sessionStorage.removeItem(KEY_ATTEMPT);
};

export function usePhoneVerificationScreenLogic() {
  const { setError } = useErrorManager();
  const navigate = useNavigate();
  const { client } = useClient();
  const [loading, setLoading] = useState(false);

  const attempt = getAttempt();

  const isPhoneHasChanged = Boolean(
    sessionStorage.getItem(SESSION_PHONE_UPDATED_KEY),
  );

  const apiPutWebcodeVerify = App.useApi(
    'client/application/verifyWebCode',
    'PUT',
  );
  const apiPostPhoneConfirmation = App.useApi(
    'client/phone-confirmation',
    'PUT',
  );

  const apiPostPhone = App.useApi('client/phone', 'POST');

  const apiPostClientApplicationWebcode = App.useApi(
    'client/application/webcode',
    'POST',
  );

  useIovation();

  const handleSubmit = async (data: PhoneVerificationForm) => {
    try {
      setLoading(true);
      const deviceId = await getFingerprintDeviceId(client?.personalId);

      const apiToUse = isPhoneHasChanged
        ? apiPostPhoneConfirmation
        : apiPutWebcodeVerify;
      const bodyProperty = isPhoneHasChanged ? 'verificationCode' : 'webCode';

      await apiToUse.fetch({
        headers: getFetchHeaders({
          [FINGERPRINT_CONSTANTS_KEYS.deviceId]: deviceId,
        }),
        body: {
          [bodyProperty]: `${data.verificationCode0}${data.verificationCode1}${data.verificationCode2}${data.verificationCode3}`,
        },
      });

      deleteSessionStorageAttempt();

      navigate(ROUTES.FIRST_LOAN_PHONE_VERIFICATION_WAITING);
    } catch (error) {
      setLoading(false);
      setError(error);

      if (attempt < 3) {
        saveAttempt(attempt + 1);
      } else {
        deleteSessionStorageAttempt();
        navigate(ROUTES.FIRST_LOAN_INCORRECT_CODE);
      }
    }
  };

  async function resendCode(): Promise<number | undefined> {
    const sessionStorageLastSMSSent = sessionStorage.getItem(
      SESSION_LAST_SMS_SENT_KEY,
    );
    if (
      sessionStorageLastSMSSent &&
      !Number.isNaN(parseInt(sessionStorageLastSMSSent)) &&
      differenceInSeconds(
        new Date().getTime(),
        parseInt(sessionStorageLastSMSSent),
      ) < COOLDOWN_SECONDS
    ) {
      return (
        COOLDOWN_SECONDS -
        differenceInSeconds(
          new Date().getTime(),
          parseInt(sessionStorageLastSMSSent),
        )
      );
    }
    sessionStorage.setItem(
      SESSION_LAST_SMS_SENT_KEY,
      new Date().getTime().toString(),
    );

    const apiToUse = isPhoneHasChanged
      ? apiPostPhone
      : apiPostClientApplicationWebcode;

    try {
      await apiToUse.fetch({
        headers: getFetchHeaders(),
        body: {
          mobilePhone: sessionStorage.getItem(SESSION_PHONE_UPDATED_KEY) || '',
        },
      });
    } catch (error) {
      setError(error);
    }
    return 60;
  }

  async function updateClientPhone(newPhoneNumber: string): Promise<void> {
    try {
      if (newPhoneNumber === client?.mobilePhone?.mobilePhone) {
        const message =
          window.location.pathname === ROUTES.FIRST_LOAN_INCORRECT_CODE
            ? 'same_phone_incorrect_code'
            : 'same_phone';
        setError(message);
        return;
      }

      // Resend webcode without the 60 seconds limitation
      await apiPostPhone.fetch({
        body: {
          mobilePhone: newPhoneNumber,
        },
      });

      const now = new Date().getTime().toString();
      sessionStorage.setItem('lastSMSSent', now);
      sessionStorage.setItem(SESSION_LAST_PHONE_CHANGE_KEY, now);

      // save phone updated (cola won't be updated until the user confirms the sms)
      sessionStorage.setItem(SESSION_PHONE_UPDATED_KEY, newPhoneNumber);

      navigate(ROUTES.FIRST_LOAN_PHONE_VERIFICATION);
    } catch (error) {
      if (isAlreadyExistsMobileError(error)) {
        setError('phone_already_exists');
      } else {
        setError(error);
      }
    }
  }

  function navigateToChangePhone() {
    navigate(ROUTES.FIRST_LOAN_PHONE_VERIFICATION_CHANGE_PHONE);
  }

  return {
    handleSubmit,
    resendCode,
    loading,
    navigateToChangePhone,
    updateClientPhone,
    initialSecondsLeftResend: getSecondsLeft(SESSION_LAST_SMS_SENT_KEY),
    initialSecondsLeftPhoneChange: getSecondsLeft(
      SESSION_LAST_PHONE_CHANGE_KEY,
    ),
  };
}
